import { mdiPencil } from "@mdi/js";
import { Button, Col, Input, ModalProps, Row, Switch } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { FailureTaskVm } from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import CustomForm from "components/form/CustomForm";
import { MaterialIcon } from "components/MaterialIcon";
import { Permission } from "components/Permission";
import CustomModal from "components/ui/CustomModal";
import Claims from "features/auth/claims";
import { FailureTaskCreateFormValues } from "features/manage-dictionaries/models";
import useEditFormModal from "hooks/useEditModal";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { GrapeAntdForm, requiredField, useGrapeAntdForm } from "widgets/form";
import { ListRequestParams } from "widgets/table/useTableUtils";

export interface FailureTaskCreateModalProps extends ModalProps {
  fetchDataParams?: ListRequestParams;
}

const FailureTaskCreateModal: React.FC<FailureTaskCreateModalProps> = ({
  onCancel,
  visible,
  fetchDataParams,
  ...rest
}) => {
  const { t } = useTranslation();
  const formUtils = useGrapeAntdForm<FailureTaskCreateFormValues>();
  const dispatch = useDispatch();
  const { editingItemId } = useSelector((state) => state.failureTaskCreate);
  const { list } = useSelector((state) => state.failureTaskList);
  const { create, edit } = useSelector(
    (state) => state.loading.effects.failureTaskCreate
  );

  const getInitialValues = useCallback(
    (itemData: FailureTaskVm): FailureTaskCreateFormValues => {
      return {
        name: itemData.name || "",
        isActive: itemData.isActive ?? true,
        description: itemData.description || undefined,
        id: itemData.id,
      };
    },
    []
  );

  const { formMode, setFormMode } = useEditFormModal<
    FailureTaskVm,
    FailureTaskCreateFormValues
  >({
    formUtils,
    getInitialValues,
    modalIsVisible: visible,
    resetItemId: dispatch.failureTaskCreate.resetEditingItemData,
    itemId: editingItemId,
    list,
  });

  const handleFinish = useCallback(
    async (values: FailureTaskCreateFormValues): Promise<void> => {
      if (formMode === "Create") {
        await dispatch.failureTaskCreate.create(values);
      }

      if (formMode === "Edit" && typeof values.id === "string") {
        await dispatch.failureTaskCreate.edit({
          id: values.id,
          values,
        });
      }

      dispatch.failureTaskList.fetchList(fetchDataParams);

      onCancel?.("" as any);
      formUtils.form.resetFields();
    },
    [
      dispatch.failureTaskCreate,
      dispatch.failureTaskList,
      fetchDataParams,
      formMode,
      formUtils.form,
      onCancel,
    ]
  );

  const handleModalOk = useCallback(() => {
    formUtils.form.submit();
  }, [formUtils.form]);

  return (
    <CustomModal
      {...rest}
      visible={visible}
      onCancel={onCancel}
      title={
        formMode === "Create"
          ? t("failureTask.createModal.title")
          : t("failureTask.editModal.title")
      }
      size="large"
      onOk={handleModalOk}
      destroyOnClose
      okButtonProps={{
        loading: create.loading || edit.loading,
        disabled: formMode === "View",
      }}
      cancelButtonProps={{ disabled: create.loading || edit.loading }}
      formInstance={formUtils.form}
    >
      <CustomForm
        formUtils={formUtils}
        onFinish={handleFinish}
        viewMode={formMode === "View"}
      >
        {formMode === "View" && (
          <Permission acceptableClaims={[Claims.FailureTask.Write]}>
            <Row justify="end">
              <Button
                icon={<MaterialIcon path={mdiPencil} />}
                onClick={() => setFormMode("Edit")}
              >
                {t("common.edit")}
              </Button>
            </Row>
          </Permission>
        )}

        <Row justify="center">
          {(formMode === "Edit" || formMode === "View") && (
            <>
              <Col span={24}>
                <GrapeAntdForm.Item
                  name="isActive"
                  valuePropName="checked"
                  label={t("failureTask.status")}
                >
                  <Switch
                    checkedChildren={t("common.active")}
                    unCheckedChildren={t("common.inactive")}
                  />
                </GrapeAntdForm.Item>
              </Col>

              <GrapeAntdForm.Item name="id" hidden>
                <Input hidden />
              </GrapeAntdForm.Item>
            </>
          )}
          <Col span={24}>
            <GrapeAntdForm.Item
              name="name"
              label={t("failureTask.name")}
              rules={[requiredField(t)]}
            >
              <Input size="large" />
            </GrapeAntdForm.Item>
          </Col>
          <Col span={24}>
            <GrapeAntdForm.Item
              name="description"
              label={t("failureTask.description")}
            >
              <TextArea size="large" maxLength={200} rows={6} />
            </GrapeAntdForm.Item>
          </Col>
        </Row>
      </CustomForm>
    </CustomModal>
  );
};

export default FailureTaskCreateModal;
